import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Divider,
  Paper,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination
} from "@mui/material";
import { Download as DownloadIcon, QrCode as QrCodeIcon } from "@mui/icons-material";
import { QRCode } from 'react-qrcode-logo';
import html2canvas from 'html2canvas';
import { Configuration } from "../../../assets/data/config";
import { apiServices } from "../../../services/apiServices";
import qrLogo from "../../../assets/images/logos/qrlogo.png";
import EntityReferralStatistics from "../../AdminDashboard/Components/EntityReferralStatistics";

const UserDetailsModal = ({ open, onClose, userId, onUserUpdated }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});

  // Referral statistics state
  const [referralStats, setReferralStats] = useState(null);
  const [referralStatsLoading, setReferralStatsLoading] = useState(false);
  const [referralStatsError, setReferralStatsError] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Download state
  const [downloadingCertificate, setDownloadingCertificate] = useState(false);
  const [downloadingVisitingCard, setDownloadingVisitingCard] = useState(false);
  const [downloadingQRCode, setDownloadingQRCode] = useState(false);

  // Fetch user details when modal opens
  useEffect(() => {
    if (open && userId) {
      fetchUserDetails();
    }
  }, [open, userId]);

  // Fetch user details from API
  const fetchUserDetails = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await apiServices.getAPI(`users/${userId}`);

      if (response.data.success) {
        const userData = response.data.data;
        setUser(userData);
        setEditData({
          username: userData.username || "",
          mobile: userData.mobile || "",
          country: userData.country || "",
          dob: userData.dob || "",
          activestatus: userData.activestatus || "Y",
        });

        // Fetch referral stats after user data is loaded
        if (userData.peacekeeper_id) {
          fetchReferralStats(userData.peacekeeper_id);
        }
      } else {
        setError("Failed to fetch user details");
      }
    } catch (err) {
      console.error("Error fetching user details:", err);
      setError("Error fetching user details. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch referral statistics
  const fetchReferralStats = async (peacekeeperId) => {
    if (!peacekeeperId) return;

    setReferralStatsLoading(true);
    setReferralStatsError("");
    try {
      const response = await apiServices.getAPI(
        `users/referral-stats/${peacekeeperId}`
      );

      if (response.data.success) {
        setReferralStats(response.data.data);
      } else {
        setReferralStatsError("Failed to fetch referral statistics");
      }
    } catch (err) {
      console.error("Error fetching referral stats:", err);
      setReferralStatsError(
        "Error fetching referral statistics. Please try again."
      );
    } finally {
      setReferralStatsLoading(false);
    }
  };

  // Handle edit mode toggle
  const handleEditToggle = () => {
    setIsEditing(!isEditing);
    if (!isEditing) {
      // Reset edit data to current user data
      setEditData({
        username: user?.username || "",
        mobile: user?.mobile || "",
        country: user?.country || "",
        dob: user?.dob || "",
        activestatus: user?.activestatus || "Y",
      });
    }
  };

  // Handle input changes
  const handleInputChange = (field, value) => {
    setEditData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle save changes
  const handleSave = async () => {
    setSaving(true);
    setError("");
    setSuccess("");

    try {
      const response = await apiServices.putAPI("update-user-info", {
        userid: userId,
        ...editData,
      });

      if (response.data.success) {
        setSuccess("User updated successfully!");
        setIsEditing(false);

        // Refresh user data
        await fetchUserDetails();

        // Notify parent component
        if (onUserUpdated) {
          onUserUpdated();
        }

        // Clear success message after delay
        setTimeout(() => setSuccess(""), 3000);
      } else {
        setError("Failed to update user");
      }
    } catch (err) {
      console.error("Error updating user:", err);
      setError("Error updating user. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setIsEditing(false);
    setError("");
    setSuccess("");
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  // Get status chip color
  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case "Y":
        return "success";
      case "N":
        return "error";
      default:
        return "default";
    }
  };

  // Get payment status chip color
  const getPaymentStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case "COMPLETED":
        return "success";
      case "PENDING":
        return "warning";
      case "FAILED":
        return "error";
      default:
        return "default";
    }
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Generate referral link based on usertype and temp_peacekeeper_id
  const generateReferralLink = () => {
    if (!user) return '';

    const domain = Configuration.frontendDomain;
    const peacekeeperId = user.peacekeeper_id || 'IN-0000000-PK';

    // Priority 1: For Entity usertype with categoryname, include category in URL
    // Format: domain/register?Name-Category-PeacekeeperID
    if (user.usertype === 'Entity' && user.categoryname) {
      const cleanName = (user.name || user.username || 'User')
        .replace(/[^a-zA-Z0-9\s]/g, '')
        .replace(/\s+/g, '-');
      const cleanCategory = user.categoryname
        .replace(/[^a-zA-Z0-9\s]/g, '')
        .replace(/\s+/g, '-');
      return `${domain}/register?${cleanName}-${cleanCategory}-${peacekeeperId}`;
    }

    // Priority 2: For users referred by entities (have temp_peacekeeper_id)
    // Format: domain/register?{temp_peacekeeper_id}
    if (user.temp_peacekeeper_id) {
      return `${domain}/register?${user.temp_peacekeeper_id}`;
    }

    // Priority 3: For regular users (no entity, no temp ID)
    // Format: domain/register?{username}-{peacekeeper_id}
    const cleanUsername = (user.username || user.name || 'User')
      .replace(/[^a-zA-Z0-9\s]/g, '')
      .replace(/\s+/g, '-');
    return `${domain}/register?${cleanUsername}-${peacekeeperId}`;
  };

  // Build filename in format: VC-name-location-ID or Certificate-name-location-ID
  const buildFilename = (prefix = 'VC') => {
    // Extract name parts for proper formatting
    const safeName = (user?.username || user?.name || 'User')
      .toString()
      .replace(/[^a-zA-Z0-9\s]/g, '') // Remove special characters but keep spaces
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
    
    // Get location (country) and clean it
    const location = (user?.country || 'Location')
      .toString()
      .replace(/[^a-zA-Z0-9\s]/g, '') // Remove special characters but keep spaces
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
    
    // Get peacekeeper ID and clean it
    const peacekeeperId = (user?.peacekeeper_id || user?.userid || 'ID').toString().trim();
    
    // Format: VC-name-location-ID or Certificate-name-location-ID
    const filename = `${prefix}-${safeName}-${location}-${peacekeeperId}`;
    
    return filename;
  };

  // Download certificate as PNG
  const handleDownloadCertificate = async () => {
    if (!user?.userid) return;
    
    setDownloadingCertificate(true);
    try {
      const response = await apiServices.getBlobAPI(`certificate/${user.userid}`);
      
      if (response.data) {
        const blob = new Blob([response.data], { type: 'image/png' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${buildFilename('Certificate')}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        setSuccess('Certificate downloaded successfully!');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Certificate not available for this user');
      }
    } catch (err) {
      console.error('Error downloading certificate:', err);
      setError('Failed to download certificate. Please try again.');
    } finally {
      setDownloadingCertificate(false);
    }
  };

  // Download visiting card as PNG
  const handleDownloadVisitingCard = async () => {
    if (!user?.userid) return;
    
    setDownloadingVisitingCard(true);
    setError('');
    setSuccess('');
    
    try {
      // First, try to fetch the visiting card
      let response;
      try {
        response = await apiServices.getBlobAPI(`visiting-card/${user.userid}`);
        
        // Check if response is empty or invalid
        if (!response.data || response.data.size === 0) {
          throw new Error('Visiting card is empty');
        }
      } catch (fetchError) {
        // If visiting card doesn't exist (404, empty, or similar), regenerate it
        const isNotFound = fetchError.response?.status === 404 || 
                          fetchError.message?.includes('not found') ||
                          fetchError.message?.includes('empty');
        
        if (!isNotFound && fetchError.response?.status !== 404) {
          // If it's a different error, throw it
          throw fetchError;
        }
        
        console.log('Visiting card not found, regenerating...');
        setSuccess('Visiting card not found. Regenerating...');
        
        // Determine which ID to use for regeneration (same as VisitingCard.js)
        const idForRegeneration = user.peacekeeper_id || user.idNumber;
        if (!idForRegeneration) {
          throw new Error('Unable to regenerate visiting card: No peacekeeper ID found');
        }
        
        // Regenerate the visiting card
        const updateResponse = await apiServices.putAPI(`update-visiting-card/${idForRegeneration}`, {
          forceRegenerate: true
        });
        
        if (updateResponse.status !== 200 && updateResponse.status !== 201) {
          throw new Error('Failed to regenerate visiting card');
        }
        
        setSuccess('Visiting card regenerated. Fetching...');
        
        // Wait for generation to complete, then fetch
        await new Promise(resolve => setTimeout(resolve, 3000));
        
        // Try fetching again after regeneration
        response = await apiServices.getBlobAPI(`visiting-card/${user.userid}`);
        
        // Check if the regenerated card is valid
        if (!response.data || response.data.size === 0) {
          throw new Error('Visiting card was regenerated but is still empty');
        }
      }
      
      if (response.data) {
        const blob = new Blob([response.data], { type: 'image/png' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${buildFilename('VC')}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        setSuccess('Visiting card downloaded successfully!');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Visiting card not available for this user');
        setTimeout(() => setError(''), 3000);
      }
    } catch (err) {
      console.error('Error downloading visiting card:', err);
      if (err.response?.status === 404 || err.message?.includes('not found')) {
        setError('Visiting card not available. Please try regenerating it manually.');
      } else {
        setError('Failed to download visiting card. Please try again.');
      }
      setTimeout(() => setError(''), 3000);
    } finally {
      setDownloadingVisitingCard(false);
    }
  };

  // Generate and download QR code from referral link
  const handleDownloadQRCode = async () => {
    if (!user) return;
    
    setDownloadingQRCode(true);
    try {
      const referralLink = generateReferralLink();
      
      if (!referralLink) {
        setError('Unable to generate referral link for this user');
        setDownloadingQRCode(false);
        return;
      }

      // Create a temporary container to render QR code
      const tempDiv = document.createElement('div');
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.style.width = '512px';
      tempDiv.style.height = '512px';
      tempDiv.style.display = 'flex';
      tempDiv.style.justifyContent = 'center';
      tempDiv.style.alignItems = 'center';
      tempDiv.style.backgroundColor = '#ffffff';
      tempDiv.style.padding = '20px';
      document.body.appendChild(tempDiv);

      // Create a React root and render QR code
      const { createRoot } = await import('react-dom/client');
      const root = createRoot(tempDiv);
      
      root.render(
        React.createElement(QRCode, {
          value: referralLink,
          size: 472,
          logoImage: qrLogo,
          logoWidth: 70,
          logoHeight: 70,
          logoOpacity: 1,
          logoPadding: 3,
          logoPaddingStyle: "circle",
          removeQrCodeTail: true,
          qrStyle: 'squares',
          eyeRadius: 10,
          fgColor: "#214F7D",
          eyeColor: ["#214F7D", "#214F7D", "#214F7D"],
          backgroundOptions: { color: "#ffffff" },
          logoOptions: {
            backgroundColor: "#ffffff",
            padding: 6,
            borderRadius: 6,
            borderWidth: 2,
            borderColor: "#214F7D",
          },
          ecLevel: 'H',
        })
      );

      // Wait for QR code to render
      await new Promise(resolve => setTimeout(resolve, 800));

      // Capture as canvas using html2canvas
      const canvas = await html2canvas(tempDiv, {
        width: 512,
        height: 512,
        backgroundColor: '#ffffff',
        scale: 2, // Higher resolution
        useCORS: true,
      });

      // Convert to blob and download
      canvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `QRCode-${buildFilename('Referral')}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          setSuccess('QR code downloaded successfully!');
          setTimeout(() => setSuccess(''), 3000);
        } else {
          setError('Failed to generate QR code image');
        }
        
        // Cleanup
        root.unmount();
        document.body.removeChild(tempDiv);
        setDownloadingQRCode(false);
      }, 'image/png', 1.0);
    } catch (err) {
      console.error('Error generating QR code:', err);
      setError('Failed to generate QR code. Please try again.');
      setDownloadingQRCode(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: "90vh",
          minWidth: "900px",
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: "1px solid #e0e0e0",
          backgroundColor: "#f8f9fa",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          py: 2.5,
        }}
      >
        <Typography variant="h5" fontWeight="300" color="text.primary">
          {isEditing ? "Edit User Profile" : "User Details"}
        </Typography>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          {/* Download buttons - only show when user data is loaded */}
          {user && !isEditing && (
            <>
              <Button
                variant="outlined"
                onClick={handleDownloadCertificate}
                disabled={downloadingCertificate}
                startIcon={downloadingCertificate ? <CircularProgress size={16} /> : <DownloadIcon />}
                size="medium"
                sx={{
                  borderColor: "#3A8139",
                  color: "#3A8139",
                  borderRadius: "50px",
                  px: 3,
                  py: 1,
                  textTransform: "none",
                  fontWeight: 500,
                  "&:hover": {
                    borderColor: "#2d652c",
                    backgroundColor: "rgba(58, 129, 57, 0.04)",
                  },
                  "&:disabled": {
                    borderColor: "#ccc",
                    color: "#999",
                  },
                }}
              >
                {downloadingCertificate ? "Downloading..." : "Download Certificate"}
              </Button>
              <Button
                variant="outlined"
                onClick={handleDownloadVisitingCard}
                disabled={downloadingVisitingCard}
                startIcon={downloadingVisitingCard ? <CircularProgress size={16} /> : <DownloadIcon />}
                size="medium"
                sx={{
                  borderColor: "#214F7D",
                  color: "#214F7D",
                  borderRadius: "50px",
                  px: 3,
                  py: 1,
                  textTransform: "none",
                  fontWeight: 500,
                  "&:hover": {
                    borderColor: "#1a3f63",
                    backgroundColor: "rgba(33, 79, 125, 0.04)",
                  },
                  "&:disabled": {
                    borderColor: "#ccc",
                    color: "#999",
                  },
                }}
              >
                {downloadingVisitingCard ? "Downloading..." : "Download Visiting Card"}
              </Button>
              <Button
                variant="outlined"
                onClick={handleDownloadQRCode}
                disabled={downloadingQRCode}
                startIcon={downloadingQRCode ? <CircularProgress size={16} /> : <QrCodeIcon />}
                size="medium"
                sx={{
                  borderColor: "#FF6B35",
                  color: "#FF6B35",
                  borderRadius: "50px",
                  px: 3,
                  py: 1,
                  textTransform: "none",
                  fontWeight: 500,
                  "&:hover": {
                    borderColor: "#E55A2B",
                    backgroundColor: "rgba(255, 107, 53, 0.04)",
                  },
                  "&:disabled": {
                    borderColor: "#ccc",
                    color: "#999",
                  },
                }}
              >
                {downloadingQRCode ? "Generating..." : "Download QR Code"}
              </Button>
            </>
          )}
          <Button
            variant="outlined"
            onClick={onClose}
            size="medium"
            sx={{
              borderColor: "#6c757d",
              color: "#6c757d",
              borderRadius: "50px",
              px: 3,
              py: 1,
              textTransform: "none",
              fontWeight: 500,
              "&:hover": {
                borderColor: "#495057",
                backgroundColor: "rgba(108, 117, 125, 0.04)",
              },
            }}
          >
            Close
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 0, pb: 0 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <Box sx={{ textAlign: "center" }}>
              <CircularProgress size={40} sx={{ mb: 2 }} />
              <Typography variant="body1" color="text.secondary">
                Loading user details...
              </Typography>
            </Box>
          </Box>
        ) : user ? (
          <>
            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                aria-label="user details tabs"
                sx={{
                  "& .MuiTab-root": {
                    textTransform: "none",
                    fontWeight: 500,
                    fontSize: "0.95rem",
                    minHeight: 48,
                  },
                }}
              >
                <Tab label="User Details" />
                <Tab label="Referral Statistics" />
                <Tab label="Entity Referral Statistics" />
              </Tabs>
            </Box>

            {/* Tab Content */}
            {activeTab === 0 && (
              <>
                {/* Alerts */}
                {error && (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                  </Alert>
                )}

                {success && (
                  <Alert severity="success" sx={{ mb: 3 }}>
                    {success}
                  </Alert>
                )}

                {/* User Information Grid Layout */}
                <Grid container spacing={2} className="mb-4">
                  {/* Basic Information Section */}
                  <Grid item xs={12} sm={6} md={6} lg={6} className="w-100">
                    <Paper
                      sx={{
                        p: 2,
                        height: "100%",
                        backgroundColor: "#f8f9fa",
                        mt: 2,
                      }}
                    >
                      <Typography
                        variant="h6"
                        gutterBottom
                        sx={{
                          color: "#214F7D",
                          fontWeight: 300,
                          mb: 2,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mt: 1,
                        }}
                      >
                        📋 Basic Information
                      </Typography>

                      <Grid container spacing={1.5}>
                        {/* Row 1: User ID | IAPM ID */}
                        <Grid item xs={12} sm={6} md={6}>
                          <Typography
                            variant=""
                            color="text.secondary"
                            gutterBottom
                            sx={{ fontSize: "0.8rem", mb: 0.5 }}
                          >
                            User ID
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              p: 1,
                              backgroundColor: "#fff",
                              borderRadius: 1,
                              border: "1px solid #e9ecef",
                            }}
                          >
                            {user.userid}
                          </Typography>
                        </Grid>

                        <Grid item xs={12} sm={6} md={6}>
                          <Typography
                            variant=""
                            color="text.secondary"
                            gutterBottom
                            sx={{ fontSize: "0.8rem", mb: 0.5 }}
                          >
                            IAPM ID
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: "bold",
                              color: "#214F7D",
                              fontSize: "0.9rem",
                              p: 1,
                              backgroundColor: "#fff",
                              borderRadius: 1,
                              border: "1px solid #e9ecef",
                            }}
                          >
                            {user.peacekeeper_id}
                          </Typography>
                        </Grid>

                        {/* Row 2: Name | Mobile Number */}
                        <Grid item xs={12} sm={6} md={6}>
                          <Typography
                            variant=""
                            color="text.secondary"
                            gutterBottom
                            sx={{ fontSize: "0.8rem", mb: 0.5 }}
                          >
                            Name
                          </Typography>
                          {isEditing ? (
                            <TextField
                              fullWidth
                              value={editData.username}
                              onChange={(e) =>
                                handleInputChange("username", e.target.value)
                              }
                              size="small"
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  backgroundColor: "#fff",
                                  "&:hover .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#214F7D",
                                  },
                                  "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                    {
                                      borderColor: "#214F7D",
                                    },
                                },
                              }}
                            />
                          ) : (
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                p: 1,
                                backgroundColor: "#fff",
                                borderRadius: 1,
                                border: "1px solid #e9ecef",
                              }}
                            >
                              {user.username}
                            </Typography>
                          )}
                        </Grid>

                        <Grid item xs={12} sm={6} md={6}>
                          <Typography
                            variant=""
                            color="text.secondary"
                            gutterBottom
                            sx={{ fontSize: "0.8rem", mb: 0.5 }}
                          >
                            Mobile Number
                          </Typography>
                          {isEditing ? (
                            <TextField
                              fullWidth
                              value={editData.mobile}
                              onChange={(e) =>
                                handleInputChange("mobile", e.target.value)
                              }
                              size="small"
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  backgroundColor: "#fff",
                                  "&:hover .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#214F7D",
                                  },
                                  "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                    {
                                      borderColor: "#214F7D",
                                    },
                                },
                              }}
                            />
                          ) : (
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                p: 1,
                                backgroundColor: "#fff",
                                borderRadius: 1,
                                border: "1px solid #e9ecef",
                              }}
                            >
                              {user.mobile || "N/A"}
                            </Typography>
                          )}
                        </Grid>

                        {/* Row 3: Country | Date of Birth */}
                        <Grid item xs={12} sm={6} md={6}>
                          <Typography
                            variant=""
                            color="text.secondary"
                            gutterBottom
                            sx={{ fontSize: "0.8rem", mb: 0.5 }}
                          >
                            Country
                          </Typography>
                          {isEditing ? (
                            <TextField
                              fullWidth
                              value={editData.country}
                              onChange={(e) =>
                                handleInputChange("country", e.target.value)
                              }
                              size="small"
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  backgroundColor: "#fff",
                                  "&:hover .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#214F7D",
                                  },
                                  "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                    {
                                      borderColor: "#214F7D",
                                    },
                                },
                              }}
                            />
                          ) : (
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                p: 1,
                                backgroundColor: "#fff",
                                borderRadius: 1,
                                border: "1px solid #e9ecef",
                              }}
                            >
                              {user.country || "N/A"}
                            </Typography>
                          )}
                        </Grid>

                        <Grid item xs={12} sm={6} md={6}>
                          <Typography
                            variant=""
                            color="text.secondary"
                            gutterBottom
                            sx={{ fontSize: "0.8rem", mb: 0.5 }}
                          >
                            Date of Birth
                          </Typography>
                          {isEditing ? (
                            <TextField
                              fullWidth
                              type="date"
                              value={editData.dob}
                              onChange={(e) =>
                                handleInputChange("dob", e.target.value)
                              }
                              size="small"
                              InputLabelProps={{ shrink: true }}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  backgroundColor: "#fff",
                                  "&:hover .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#214F7D",
                                  },
                                  "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                    {
                                      borderColor: "#214F7D",
                                    },
                                },
                              }}
                            />
                          ) : (
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                p: 1,
                                backgroundColor: "#fff",
                                borderRadius: 1,
                                border: "1px solid #e9ecef",
                              }}
                            >
                              {formatDate(user.dob)}
                            </Typography>
                          )}
                        </Grid>

                        {/* Row 4: Email | Account Status */}
                        <Grid item xs={12} sm={6} md={6}>
                          <Typography
                            variant=""
                            color="text.secondary"
                            gutterBottom
                            sx={{ fontSize: "0.8rem", mb: 0.5 }}
                          >
                            Email
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#6c757d",
                              p: 1,
                              backgroundColor: "#fff",
                              borderRadius: 1,
                              border: "1px solid #e9ecef",
                            }}
                          >
                            {user.email}
                          </Typography>
                        </Grid>

                        <Grid
                          item
                          xs={12}
                          sm={6}
                          md={6}
                          flexDirection="column"
                          sx={{ flexDirection: "column" }}
                          className="d-flex-align-center-justify-center"
                        >
                          <Typography
                            variant=""
                            color="text.secondary"
                            gutterBottom
                            sx={{ fontSize: "0.8rem", mb: 0.5 }}
                          >
                            Account Status
                          </Typography>
                          {isEditing ? (
                            <FormControl fullWidth size="small">
                              <Select
                                value={editData.activestatus}
                                onChange={(e) =>
                                  handleInputChange(
                                    "activestatus",
                                    e.target.value
                                  )
                                }
                                sx={{
                                  backgroundColor: "#fff",
                                  "& .MuiOutlinedInput-root": {
                                    borderColor: "#dee2e6",
                                  },
                                  "&:hover .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#214F7D",
                                  },
                                  "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                    {
                                      borderColor: "#214F7D",
                                    },
                                }}
                              >
                                <MenuItem value="Y">Active</MenuItem>
                                <MenuItem value="N">Inactive</MenuItem>
                              </Select>
                            </FormControl>
                          ) : (
                            <Chip
                              label={
                                user.activestatus === "Y"
                                  ? "Active"
                                  : "Inactive"
                              }
                              color={getStatusColor(user.activestatus)}
                              size="small"
                              sx={{
                                fontWeight: 600,
                                fontSize: "0.75rem",
                                px: 1.5,
                                py: 0.5,
                              }}
                            />
                          )}
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>

                  {/* Additional Information Section */}
                  <Grid item xs={12} sm={6} md={6} lg={6} className="w-100">
                    <Paper
                      sx={{
                        p: 2,
                        width: "100%",
                        backgroundColor: "#f8f9fa",
                        mt: 2,
                        mb: 2,
                      }}
                    >
                      <Typography
                        variant="h6"
                        gutterBottom
                        sx={{
                          color: "#214F7D",
                          fontWeight: 300,
                          mb: 2,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        📊 Additional Information
                      </Typography>

                      <Grid container spacing={1.5}>
                        {/* Row 1: Payment Status | Account Type */}

                        {/* Row 2: Referred By | Discount Coupon */}
                        <Grid item xs={12} sm={6} md={6}>
                          <Typography
                            variant=""
                            color="text.secondary"
                            gutterBottom
                            sx={{ fontSize: "0.8rem", mb: 0.5 }}
                          >
                            Referred By
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              p: 1,
                              backgroundColor: "#fff",
                              borderRadius: 1,
                              border: "1px solid #e9ecef",
                              color: user.reference_by ? "#214F7D" : "#6c757d",
                            }}
                          >
                            {user.reference_by || "N/A"}
                          </Typography>
                        </Grid>

                       

                        {/* Row 3: Created Date | Last Updated */}
                        <Grid item xs={12} sm={6} md={6}>
                          <Typography
                            variant=""
                            color="text.secondary"
                            gutterBottom
                            sx={{ fontSize: "0.8rem", mb: 0.5 }}
                          >
                            Created Date
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              p: 1,
                              backgroundColor: "#fff",
                              borderRadius: 1,
                              border: "1px solid #e9ecef",
                            }}
                          >
                            {formatDate(user.created_at)}
                          </Typography>
                        </Grid>

                        <Grid item xs={12} sm={6} md={6}>
                          <Typography
                            variant=""
                            color="text.secondary"
                            gutterBottom
                            sx={{ fontSize: "0.8rem", mb: 0.5 }}
                          >
                            Last Updated
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              p: 1,
                              backgroundColor: "#fff",
                              borderRadius: 1,
                              border: "1px solid #e9ecef",
                            }}
                          >
                            {formatDate(user.updated_at)}
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          md={6}
                          sx={{ flexDirection: "column" }}
                          className="d-flex-align-center-justify-center"
                        >
                          <Typography
                            variant=""
                            color="text.secondary"
                            gutterBottom
                            sx={{ fontSize: "0.8rem", mb: 0.5 }}
                          >
                            Payment Status
                          </Typography>
                          <FormControl fullWidth size="small">
                            <Select
                              value={user.payment_status || 'PENDING'}
                              onChange={async (e) => {
                                const newStatus = e.target.value;
                                setSaving(true);
                                setError('');
                                setSuccess('');
                                
                                try {
                                  // Update payment status via API
                                  const response = await apiServices.putAPI('users/enhanced', {
                                    peacekeeper_id: user.peacekeeper_id,
                                    payment_status: newStatus
                                  });
                                  
                                  if (response.data.message && response.data.message.includes('successfully')) {
                                    // Update local user state
                                    setUser(prev => ({
                                      ...prev,
                                      payment_status: newStatus
                                    }));
                                    
                                    setSuccess('Payment status updated successfully!');
                                    
                                    // Notify parent component to refresh list
                                    if (onUserUpdated) {
                                      onUserUpdated();
                                    }
                                    
                                    // Clear success message after 3 seconds
                                    setTimeout(() => setSuccess(''), 3000);
                                  } else {
                                    throw new Error('Failed to update payment status');
                                  }
                                } catch (error) {
                                  console.error('Error updating payment status:', error);
                                  setError('Failed to update payment status. Please try again.');
                                } finally {
                                  setSaving(false);
                                }
                              }}
                              disabled={saving}
                              sx={{
                                backgroundColor: "#fff",
                                "& .MuiOutlinedInput-root": {
                                  borderColor: "#dee2e6",
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#214F7D",
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#214F7D",
                                },
                              }}
                            >
                              <MenuItem value="PENDING">PENDING</MenuItem>
                              <MenuItem value="COMPLETED">COMPLETED</MenuItem>
                              <MenuItem value="COMPLIMENTARY">COMPLIMENTARY</MenuItem>
                              <MenuItem value="FAILED">FAILED</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                </Grid>
              </>
            )}

            {/* Referral Statistics Tab */}
            {activeTab === 1 && (
              <>
                {referralStatsLoading ? (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", py: 6 }}
                  >
                    <Box sx={{ textAlign: "center" }}>
                      <CircularProgress size={40} sx={{ mb: 2 }} />
                      <Typography variant="body1" color="text.secondary">
                        Loading referral statistics...
                      </Typography>
                    </Box>
                  </Box>
                ) : referralStatsError ? (
                  <Box sx={{ textAlign: "center", py: 6 }}>
                    <Typography variant="h6" color="error" sx={{ mb: 2 }}>
                      {referralStatsError}
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={() =>
                        user?.peacekeeper_id &&
                        fetchReferralStats(user.peacekeeper_id)
                      }
                      size="medium"
                      sx={{ mt: 2 }}
                    >
                      Retry
                    </Button>
                  </Box>
                ) : referralStats ? (
                  <Grid container spacing={2} mt={2} mb={4}>
                    {/* 📊 Referral Statistics Section */}
                    <Grid item xs={12}>
                      <Typography
                        variant="h6"
                        gutterBottom
                        sx={{
                          color: "#214F7D",
                          fontWeight: 600,
                          mb: 2,
                        }}
                      >
                        📊 Referral Statistics
                      </Typography>
                      <Grid container spacing={1.5}>
                        {/* Total Referral */}
                        <Grid item xs={12} sm={6} md={4}>
                          <Paper
                            sx={{
                              p: 2,
                              textAlign: "center",
                              backgroundColor: "#fff",
                              border: "1px solid #e9ecef",
                              boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                            }}
                          >
                            <Typography
                              variant="h3"
                              sx={{ color: "#214F7D", fontWeight: "bold", fontSize: "2rem" }}
                            >
                              {referralStats.summary?.totalReferrals || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, fontSize: "0.9rem" }}>
                              Total Referral
                            </Typography>
                          </Paper>
                        </Grid>

                        {/* Register User */}
                        <Grid item xs={12} sm={6} md={4}>
                          <Paper
                            sx={{
                              p: 2,
                              textAlign: "center",
                              backgroundColor: "#fff",
                              border: "1px solid #e9ecef",
                              boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                            }}
                          >
                            <Typography
                              variant="h3"
                              sx={{ color: "#28a745", fontWeight: "bold", fontSize: "2rem" }}
                            >
                              {referralStats.summary?.activeReferrals || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, fontSize: "0.9rem" }}>
                              Register User
                            </Typography>
                          </Paper>
                        </Grid>

                        {/* Last Update */}
                        <Grid item xs={12} sm={6} md={4}>
                          <Paper
                            sx={{
                              p: 2,
                              textAlign: "center",
                              backgroundColor: "#fff",
                              border: "1px solid #e9ecef",
                              boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                            }}
                          >
                            <Typography
                              variant="h4"
                              sx={{ color: "#17a2b8", fontWeight: "bold", fontSize: "1.5rem" }}
                            >
                              {referralStats.lastUpdated ? 
                                new Date(referralStats.lastUpdated).toLocaleDateString() : 'N/A'
                              }
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, fontSize: "0.9rem" }}>
                              Last Update
                            </Typography>
                          </Paper>
                        </Grid>
                      </Grid>
                    </Grid>

                    {/* 🌍 Country Distribution Section */}
                    <Grid item xs={12} mt={3}>
                      <Typography variant="h6" gutterBottom sx={{ 
                        color: '#214F7D', 
                        fontWeight: 600,
                        mb: 2,
                        fontSize: '1rem'
                      }}>
                        🌍 Country Distribution
                      </Typography>
                      <Paper sx={{ 
                        width: '100%', 
                        mb: 2,
                        border: '2px solid #214F7D',
                        boxShadow: '0 4px 8px rgba(33, 79, 125, 0.2)',
                        backgroundColor: '#ffffff'
                      }}>
                        <TableContainer sx={{ 
                          maxHeight: 400,
                          border: '2px solid #214F7D',
                          borderRadius: '8px',
                          backgroundColor: '#fafafa'
                        }}>
                          <Table stickyHeader aria-label="country distribution table">
                            <TableHead>
                              <TableRow sx={{ 
                                backgroundColor: '#e3f2fd', 
                                borderBottom: '3px solid #214F7D',
                                '& th': {
                                  color: '#214F7D',
                                  fontWeight: 'bold',
                                  fontSize: '0.9rem',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px'
                                }
                              }}>
                                <TableCell sx={{ 
                                  borderBottom: '3px solid #214F7D',
                                  padding: '16px 20px'
                                }}>
                                  🌏 Country
                                </TableCell>
                                <TableCell sx={{ 
                                  borderBottom: '3px solid #214F7D',
                                  padding: '16px 20px'
                                }}>
                                  📊 Referral Count
                                </TableCell>
                                <TableCell sx={{ 
                                  borderBottom: '3px solid #214F7D',
                                  padding: '16px 20px'
                                }}>
                                  📈 Percentage
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {referralStats.countryDistribution?.length > 0 ? (
                                referralStats.countryDistribution
                                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                  .map((country, index) => {
                                    const totalCount = referralStats.summary?.totalReferrals || 0;
                                    const percentage = totalCount > 0 ? 
                                      ((parseInt(country.count) / totalCount) * 100).toFixed(1) : '0.0';
                                    
                                    return (
                                      <TableRow 
                                        key={index} 
                                        hover
                                        sx={{
                                          '&:hover': {
                                            backgroundColor: '#e3f2fd !important'
                                          },
                                          '&:nth-of-type(odd)': {
                                            backgroundColor: '#f8f9fa'
                                          },
                                          '&:nth-of-type(even)': {
                                            backgroundColor: '#ffffff'
                                          }
                                        }}
                                      >
                                        <TableCell sx={{ 
                                          fontWeight: 600, 
                                          fontSize: '0.9rem',
                                          borderBottom: '1px solid #e0e0e0',
                                          padding: '16px 20px',
                                          color: '#333333'
                                        }}>
                                          {country.country}
                                        </TableCell>
                                        <TableCell sx={{ 
                                          fontWeight: 'bold', 
                                          color: '#214F7D', 
                                          fontSize: '0.9rem',
                                          borderBottom: '1px solid #e0e0e0',
                                          padding: '16px 20px'
                                        }}>
                                          {country.count}
                                        </TableCell>
                                        <TableCell sx={{ 
                                          fontWeight: 600, 
                                          color: '#17a2b8', 
                                          fontSize: '0.9rem',
                                          borderBottom: '1px solid #e0e0e0',
                                          padding: '16px 20px'
                                        }}>
                                          {percentage}%
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })
                              ) : (
                                <TableRow>
                                  <TableCell 
                                    colSpan={3} 
                                    sx={{ 
                                      textAlign: 'center', 
                                      color: '#666666', 
                                      fontSize: '0.9rem',
                                      padding: '24px 20px',
                                      borderBottom: '1px solid #e0e0e0',
                                      backgroundColor: '#f8f9fa'
                                    }}
                                  >
                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                      No country distribution data available
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#888888', mt: 1 }}>
                                      The API response doesn't contain country distribution data
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        <TablePagination
                          rowsPerPageOptions={[5, 10, 25]}
                          component="div"
                          count={referralStats.countryDistribution?.length || 0}
                          rowsPerPage={rowsPerPage}
                          page={page}
                          onPageChange={(event, newPage) => setPage(newPage)}
                          onRowsPerPageChange={(event) => {
                            setRowsPerPage(parseInt(event.target.value, 10));
                            setPage(0);
                          }}
                          sx={{
                            '& .MuiTablePagination-actions': {
                              color: '#214F7D'
                            },
                            '& .MuiTablePagination-selectIcon': {
                              color: '#214F7D'
                            },
                            '& .MuiTablePagination-displayedRows': {
                              color: '#666666',
                              fontWeight: 500
                            },
                            '& .MuiTablePagination-select': {
                              color: '#214F7D',
                              fontWeight: 600
                            },
                            borderTop: '2px solid #214F7D',
                            backgroundColor: '#f8f9fa'
                          }}
                        />
                      </Paper>
                    </Grid>
                  </Grid>
                ) : (
                  <Box sx={{ textAlign: "center", py: 6 }}>
                    <Typography
                      variant="h6"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      No referral statistics available
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      This user hasn't made any referrals yet.
                    </Typography>
                  </Box>
                )}
              </>
            )}

            {/* Entity Referral Statistics Tab */}
            {activeTab === 2 && (
              <Box sx={{ mt: 2, mb: 4 }}>
                <EntityReferralStatistics />
              </Box>
            )}
          </>
        ) : (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
              No user data available
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please try refreshing the page or contact support if the issue
              persists.
            </Typography>
          </Box>
        )}
      </DialogContent>

      {/* Dialog Actions */}
      {isEditing && (
        <DialogActions
          sx={{
            p: 3,
            borderTop: "1px solid #e0e0e0",
            backgroundColor: "#f8f9fa",
            gap: 2,
          }}
        >
          <Button
            onClick={handleCancelEdit}
            disabled={saving}
            variant="outlined"
            size="large"
            sx={{
              borderColor: "#6c757d",
              color: "#6c757d",
              borderRadius: "50px",
              px: 4,
              py: 1.5,
              textTransform: "none",
              fontWeight: 500,
              "&:hover": {
                borderColor: "#495057",
                backgroundColor: "rgba(108, 117, 125, 0.04)",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            variant="contained"
            size="large"
            sx={{
              background: "linear-gradient(to right, #214F7D, #3A8139)",
              color: "white",
              borderRadius: "50px",
              px: 4,
              py: 1.5,
              textTransform: "none",
              fontWeight: 600,
              "&:hover": {
                background: "linear-gradient(to right, #1a3f63, #2d6a2e)",
              },
              "&:disabled": {
                background: "#ccc",
                color: "#666",
              },
            }}
          >
            {saving ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={20} color="inherit" />
                Saving Changes...
              </Box>
            ) : (
              "Save Changes"
            )}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default UserDetailsModal;
